// /src/store/mediaManagment/actions.ts

import { Media } from '../../dataTypes/mediaManagment/types';


import {
  FETCH_MEDIA_REQUEST,
  FETCH_MEDIA_SUCCESS,
  FETCH_MEDIA_FAILURE,
  FETCH_MEDIA_BY_ID_REQUEST,
  FETCH_MEDIA_BY_ID_SUCCESS,
  FETCH_MEDIA_BY_ID_FAILURE,
  FETCH_MEDIA_BY_TYPE_REQUEST,
  FETCH_MEDIA_BY_TYPE_SUCCESS,
  FETCH_MEDIA_BY_TYPE_FAILURE,
  MediaActionTypes,
  GetMediaListParams
} from './types';

import { Dispatch } from 'redux';
import { RootState } from '../generalReducer';
import { store } from '../store';

import { getMediaList, getMediaById, getMediaListByType } from '../../backendServices/mediaManagment/api';

export const fetchMediaList = (params: GetMediaListParams = {}, filter : string[] = [] ,renew: boolean = false, adminId?: number) => async (dispatch: Dispatch<MediaActionTypes>, getState: () => RootState) => {

  const state = getState();
  const lastFetched = state.mediaListReducer.lastFetchTimestamp;
  const lastFetchParams = state.mediaListReducer.lastFetchParams;
  const cacheDuration = 10 * 60 * 1000; // 10 minutes en millisecondes

  const isParamsIncluded = (lastFetchParams?.mediaType && params.mediaType)
    ? (params.mediaType === lastFetchParams.mediaType)
    : false;

  // Le cache n'est pas indexé par adminId : on le contourne dès qu'un filtre adminId est demandé
  if (lastFetched && Date.now() - lastFetched < cacheDuration && isParamsIncluded && !renew && !adminId) {

    return;
  }


  dispatch({ type: FETCH_MEDIA_REQUEST, payload: params });

  try {
    const response = await getMediaList(filter, adminId);

    if (response.code === 1) {
      dispatch({
        type: FETCH_MEDIA_SUCCESS,
        payload: {
          medias: response.payload,
          lastFetchTimestamp: Date.now(),
          lastFetchParams: params
        }
      });
    } else {
      dispatch({ type: FETCH_MEDIA_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_MEDIA_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchMediaById = (mediaId: number) => async (dispatch: Dispatch<MediaActionTypes>, getState: () => RootState) => {
  const state = getState();
  const media = state.mediaListReducer.medias;

  dispatch({ type: FETCH_MEDIA_BY_ID_REQUEST });

  if (media.has(mediaId)) {
    return;
  }

  try {
    const response = await getMediaById(mediaId);

    if (response.code === 1) {
      dispatch({ type: FETCH_MEDIA_BY_ID_SUCCESS, payload: response.payload });
    } else {
      dispatch({ type: FETCH_MEDIA_BY_ID_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_MEDIA_BY_ID_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchMediaListByType = (mediaType: string) => async (dispatch: Dispatch<MediaActionTypes>, getState: () => RootState) => {
  const state = getState();
  const media = state.mediaListReducer.medias;

  dispatch({ type: FETCH_MEDIA_BY_TYPE_REQUEST });

  const mediaArray = Array.from(media.values());
  const filteredMedia = mediaArray.filter(m => m.mediaType === mediaType);

  if (filteredMedia.length > 0) {
    dispatch({ type: FETCH_MEDIA_BY_TYPE_SUCCESS, payload: filteredMedia });
    return;
  }

  try {
    const response = await getMediaListByType(mediaType);

    if (response.code === 1) {
      dispatch({ type: FETCH_MEDIA_BY_TYPE_SUCCESS, payload: response.payload });
    } else {
      dispatch({ type: FETCH_MEDIA_BY_TYPE_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_MEDIA_BY_TYPE_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const retrieveMediaById = async (mediaId: number): Promise<Media | null> => {
  const { dispatch, getState } = store;
  const state = getState();
  const media = state.mediaListReducer.medias;

  if (media.has(mediaId)) {
    return media.get(mediaId) ?? null;
  }

  await dispatch(fetchMediaById(mediaId));
  const updatedState = getState();
  return updatedState.mediaListReducer.medias.get(mediaId) ?? null;
};

export const retrieveMediaListByType = async (mediaType: string): Promise<Media[]> => {
  const { dispatch, getState } = store;
  const state = getState();
  const media = state.mediaListReducer.medias;

  const mediaArray = Array.from(media.values());
  const filteredMedia = mediaArray.filter(m => m.mediaType === mediaType);

  if (filteredMedia.length > 0) {
    return filteredMedia;
  }

  await dispatch(fetchMediaListByType(mediaType));
  const updatedState = getState();
  return Array.from(updatedState.mediaListReducer.medias.values()).filter(m => m.mediaType === mediaType);
};

export const retrieveMediaList = async (params: GetMediaListParams = {}, filter: string[]=[], renew:boolean= false, adminId?: number): Promise<Media[]> => {

  const { dispatch, getState } = store;
  const state = getState();
  const media = state.mediaListReducer.medias;

  // Le cache global n'est pas indexé par adminId : on le contourne entièrement dans ce cas
  if (adminId) {
    const response = await getMediaList(filter, adminId);
    return response.code === 1 ? response.payload : [];
  }

  if (media.size > 0 && !renew) {
    return Array.from(media.values());
  }

  await dispatch(fetchMediaList(params, filter, renew));

  const updatedState = getState();
  return Array.from(updatedState.mediaListReducer.medias.values());
};
